/**
 * WhyCustomersLeaveWidget — dashboard card showing top cancellation reasons
 * + refund total for the last 7 days. Super-admin only.
 * Restyled in Iter 154 (Feb 27 2026) to match the Organic Luxe aesthetic.
 */

import { useEffect, useState } from "react";
import { websiteOrdersApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AlertOctagon, TrendingUp, TrendingDown, Loader2, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

export default function WhyCustomersLeaveWidget() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== "super_admin") return;
    let cancelled = false;
    websiteOrdersApi
      .cancellationSummary(7)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user?.role]);

  if (user?.role !== "super_admin") return null;
  if (!loading && (!data || data.total_cancellations === 0)) return null;

  const wow = data?.wow_pct;
  const wowIsUp = typeof wow === "number" && wow > 0;
  const wowIsDown = typeof wow === "number" && wow < 0;

  return (
    <section className="dash-card" data-testid="why-customers-leave-widget">
      <div className="dash-card-head">
        <div>
          <p className="dash-section-eyebrow" style={{ marginBottom: 6 }}>Attention</p>
          <h3 className="flex items-center gap-2">
            <AlertOctagon size={18} className="text-[--rose]" /> Why customers leave
          </h3>
          <p>Last 7 days · cancellations &amp; refunds</p>
        </div>
        <Link
          to="/leads"
          className="text-[11px] font-semibold text-[--accent] hover:underline inline-flex items-center gap-1"
          data-testid="why-leave-link"
        >
          Audit <ArrowUpRight size={12} />
        </Link>
      </div>

      <div className="dash-card-body">
        {loading ? (
          <div className="flex items-center justify-center h-16">
            <Loader2 size={16} className="animate-spin text-[--accent]" />
          </div>
        ) : (
          <>
            <div className="flex items-baseline gap-4 flex-wrap">
              <div
                className="text-5xl leading-none tabular-nums text-[--text-primary]"
                style={{ fontFamily: "Playfair Display, serif" }}
                data-testid="why-leave-total-cancellations"
              >
                {data.total_cancellations}
              </div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-[--text-secondary] font-medium">
                cancellation{data.total_cancellations === 1 ? "" : "s"}
              </div>
              <div
                className="text-[15px] font-semibold text-[--text-primary] tabular-nums ml-2"
                style={{ fontFamily: "Playfair Display, serif" }}
                data-testid="why-leave-refund-total"
              >
                ₹{Math.round(data.total_refund_inr || 0).toLocaleString("en-IN")}
                <span className="text-[10px] text-[--text-muted] uppercase tracking-[0.18em] ml-1 font-medium">refunded</span>
              </div>
              {typeof wow === "number" && (
                <span
                  className={`dash-delta-pill ${wowIsUp ? "dash-delta-pill--down" : wowIsDown ? "dash-delta-pill--up" : "dash-delta-pill--flat"}`}
                  title={`vs. prior 7 days (${data.prev_cancellations} cancellations)`}
                  data-testid="why-leave-wow-badge"
                >
                  {wowIsUp ? <TrendingUp size={10} /> : wowIsDown ? <TrendingDown size={10} /> : null}
                  {wow > 0 ? "+" : ""}{wow}% WoW
                </span>
              )}
            </div>

            {(data.top_reasons || []).length > 0 && (
              <div className="mt-6 pt-5 border-t border-[--border]">
                <p className="text-[10px] uppercase tracking-[0.24em] text-[--accent] font-semibold mb-3">
                  Top reasons
                </p>
                <div className="space-y-2.5">
                  {data.top_reasons.map((r) => {
                    const pct = data.total_cancellations > 0
                      ? Math.round((r.count / data.total_cancellations) * 100)
                      : 0;
                    return (
                      <div
                        key={r.code}
                        className="flex items-center gap-4"
                        data-testid={`why-leave-reason-${r.code}`}
                      >
                        <span className="text-[12px] text-[--text-primary] font-medium min-w-[140px] truncate">
                          {r.label}
                        </span>
                        <div className="flex-1 h-1.5 bg-[--border] rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${Math.max(pct, 4)}%`,
                              background: "linear-gradient(90deg, var(--accent-soft), var(--accent))",
                            }}
                          />
                        </div>
                        <span className="text-[11px] font-semibold text-[--text-primary] tabular-nums whitespace-nowrap">
                          {r.count} · ₹{Math.round(r.refund_inr).toLocaleString("en-IN")}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
